import React from "react";
import { DataTable } from "../components/DataTable";
import { Link } from "react-router-dom";
import { getOrganizations } from "../services/momangAdminService";
import { DateTime } from "luxon";
import { useQuery } from "react-query";
import { Message } from "semantic-ui-react";
import { getAiMonthlyBudgetStatuses } from "../services/analyticsService";

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value) {
  if (value === null || value === undefined) return "-";
  return usdFormatter.format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return "-";
  return `${value.toFixed(1)}%`;
}

function getOrganizationAiBudgetOverview(organization, budgetStatus) {
  if (!budgetStatus) return { currentMonthAiSpendUsd: 0, aiMonthlyBudgetUsagePercent: 0 };

  const baseUsd = organization.aiMonthlyBudgetBaseUsdOverride ?? budgetStatus.calculatedBaseUsd;
  const perUserUsd = organization.aiMonthlyBudgetPerUserUsdOverride ?? budgetStatus.calculatedPerUserUsd;
  const monthlyBudgetUsd = baseUsd + budgetStatus.activeUserCount * perUserUsd;
  const aiMonthlyBudgetUsagePercent =
    monthlyBudgetUsd > 0 ? (budgetStatus.currentMonthSpendUsd / monthlyBudgetUsd) * 100 : 0;

  return {
    currentMonthAiSpendUsd: budgetStatus.currentMonthSpendUsd,
    aiMonthlyBudgetUsagePercent,
  };
}

function withAiBudgetOverviews(organizations = [], budgetStatuses = []) {
  const budgetStatusByOrganization = new Map(
    budgetStatuses.map((budgetStatus) => [budgetStatus.organizationSlug, budgetStatus])
  );

  return organizations.map((organization) => ({
    ...organization,
    ...getOrganizationAiBudgetOverview(organization, budgetStatusByOrganization.get(organization.slug)),
  }));
}

export const Organizations = () => {
  const {
    isLoading,
    error,
    data: organizations,
  } = useQuery("organizations", getOrganizations, {
    refetchInterval: (data) => (data?.some((org) => ["queued", "running"].includes(org.deletionStatus)) ? 5000 : false),
  });
  const {
    isLoading: isLoadingAiBudgetStatuses,
    error: aiBudgetStatusesError,
    data: aiBudgetStatuses,
  } = useQuery("ai-monthly-budget-statuses", getAiMonthlyBudgetStatuses);
  const organizationsWithAiBudget = withAiBudgetOverviews(organizations, aiBudgetStatuses);

  const columns = [
    { name: "name", header: "Name", content: ({ name, slug }) => <Link to={`/organizations/${slug}`}>{name}</Link> },
    { name: "plan", header: "Plan" },
    { name: "deletionStatus", header: "Deletion", content: ({ deletionStatus }) => deletionStatus || "—" },
    {
      name: "numberOfUsers",
      header: "Number of users",
      content: ({ numberOfUsers, slug }) => <Link to={`/users?organizationSlug=${slug}`} children={numberOfUsers} />,
    },
    {
      name: "currentMonthAiSpendUsd",
      header: "Current month AI usage",
      content: ({ currentMonthAiSpendUsd }) => formatUsd(currentMonthAiSpendUsd),
    },
    {
      name: "aiMonthlyBudgetUsagePercent",
      header: "AI budget used",
      content: ({ aiMonthlyBudgetUsagePercent }) => formatPercent(aiMonthlyBudgetUsagePercent),
    },
    {
      name: "createdAt",
      header: "Created At",
      content: ({ createdAt }) => DateTime.fromISO(createdAt).toFormat("yyyy-MM-dd"),
    },
  ];

  if (error) return <Message error visible content={error.message} />;
  if (aiBudgetStatusesError) return <Message error visible content={aiBudgetStatusesError.message} />;

  return (
    <>
      <h2>Organizations</h2>
      <DataTable
        items={organizationsWithAiBudget}
        columns={columns}
        loading={isLoading || isLoadingAiBudgetStatuses}
        fixed={false}
      />
    </>
  );
};
